/**
 * GrownUpsPanel - what the engine currently believes, in plain words.
 *
 * Opened by pressing and holding the dots in the corner. It exists because a
 * game that plugs into a measurement framework has to be inspectable during a
 * session: a therapist watching a child needs to know which rung the prompt is
 * on and which boxes this level can even record, and a developer needs to see
 * that attempts are actually reaching the chart.
 *
 * Everything here is READ from the engine - the level intent, the prompt style
 * and what it must include, the strictness band, the box list and who judges
 * each one. None of it is restated by the game, so it cannot drift.
 */

import { getSupportLevel } from '../../../../engine/framework.js';
import { redactDeep } from '../../../../engine/language.js';

export default function GrownUpsPanel({
  game, child, placement, position, engineFacts, supportIndex,
  recommendation, levelState, profilePosition, queue, rejections, stats, soundOn, mic,
  onToggleSound, onClose, onEndSession, onNextLevel,
}) {
  const rung = getSupportLevel(supportIndex);

  return (
    <div className="bp-panel" role="dialog" aria-label="Grown-ups panel">
      <div className="bp-panel__sheet">
        <header className="bp-panel__head">
          <h2>{game.name}</h2>
          <button type="button" className="bp__btn bp__btn--quiet" onClick={onClose}>Close</button>
        </header>

        <section className="bp-panel__block">
          <h3>Where we are</h3>
          <p className="bp-panel__lede">
            {child ? child.name : 'Practice mode'} ·{' '}
            {position ? `Phase ${position.phase}, level ${position.level}` : '—'}
          </p>
          {engineFacts && (
            <ul className="bp-panel__list">
              <li><b>{engineFacts.level.phaseName}</b> — {engineFacts.level.intent}</li>
              <li>MLU target {engineFacts.level.mlu} · {engineFacts.level.operant}</li>
              <li>Prompt style: {engineFacts.prompt.style} — must include: {engineFacts.prompt.mustInclude}</li>
              <li>Recognition: {engineFacts.strictness.id} — {engineFacts.strictness.accepts}</li>
              <li>Prompt now: <b>{rung.name}</b> — {rung.detail}</li>
            </ul>
          )}
          {profilePosition && (
            <p className="bp-panel__fine">
              On the child’s profile: phase {profilePosition.phase}, level {profilePosition.level}
              {profilePosition.furthest && (
                profilePosition.furthest.phase !== profilePosition.phase
                || profilePosition.furthest.level !== profilePosition.level
              ) && ` · furthest reached: phase ${profilePosition.furthest.phase}, level ${profilePosition.furthest.level}`}
              . This is what another game would place them at.
            </p>
          )}
          {placement && placement.warning && (
            <p className="bp-panel__warn">{placement.warning}</p>
          )}
        </section>

        <section className="bp-panel__block">
          <h3>What this level records</h3>
          <ul className="bp-panel__boxes">
            {engineFacts && engineFacts.boxes.map((box) => (
              <li key={box.boxId} data-state={box.state}>
                <span className="bp-panel__box-id">{box.boxId}</span>
                <span className="bp-panel__box-name">{box.name}</span>
                <span className="bp-panel__box-by">
                  {box.judgedBy === 'nobody'
                    ? (box.state === 'observational' ? 'not measurable from speech' : 'not instrumented')
                    : `judged by the ${box.judgedBy}`}
                </span>
              </li>
            ))}
          </ul>
          <p className="bp-panel__fine">
            A box needs ten first attempts before it takes a colour, and a level
            is only green when every box that can be measured is green.
          </p>
          {levelState && (
            <p className={`bp-panel__lede${levelState.mastered ? ' bp-panel__lede--green' : ''}`}>
              This level is <b>{levelState.color}</b>
              {' '}({levelState.greenBoxes} of {levelState.scoredBoxes} boxes green)
              {levelState.mastered && ' — finished. The next level is being offered.'}
            </p>
          )}
        </section>

        <section className="bp-panel__block">
          <h3>Microphone</h3>
          <ul className="bp-panel__list">
            <li>Recognition: {mic.listening ? 'listening' : 'not listening'}</li>
            <li>
              Sound level monitoring: {mic.monitored ? 'on' : `off (${mic.permission})`}
              {mic.monitored && mic.noiseFloor !== null && ` · room floor ${mic.noiseFloor.toFixed(4)}`}
            </li>
          </ul>
          {mic.error && <p className="bp-panel__warn">{mic.error}</p>}
          {!mic.monitored && (
            <p className="bp-panel__fine">
              Without sound monitoring, a vocalisation that is not a word cannot
              be detected, and Phase 1 “attempts vocalization” falls back to
              whether speech recognition heard anything.
            </p>
          )}
        </section>

        <section className="bp-panel__block">
          <h3>Pacing</h3>
          {recommendation ? (
            <>
              <p className="bp-panel__lede">{recommendation.action.replace(/_/g, ' ')}</p>
              <p className="bp-panel__fine">{recommendation.reason}</p>
              <p className="bp-panel__fine">
                Prompt is {recommendation.atTargetSupport
                  ? 'at the level’s own rung'
                  : 'above the level’s own rung, and will fade as the child succeeds'}
                {' · '}source: {recommendation.source}
              </p>
            </>
          ) : (
            <p className="bp-panel__fine">Not enough turns yet at this level.</p>
          )}
        </section>

        <section className="bp-panel__block">
          <h3>Recording</h3>
          {queue.practice ? (
            <p className="bp-panel__fine">
              Practice mode — nothing is being written to a chart.
            </p>
          ) : (
            <ul className="bp-panel__list">
              <li>{queue.sent} sent · {queue.pending} waiting · {queue.rejected} refused</li>
              {queue.lastError && (
                <li className="bp-panel__warn">
                  Last error: {queue.lastError.status || 'network'} {queue.lastError.message}
                </li>
              )}
              {queue.stopped && <li className="bp-panel__warn">Recording stopped: {queue.stoppedReason}</li>}
            </ul>
          )}
          {rejections && rejections.length > 0 && (
            <details className="bp-panel__details">
              <summary>{rejections.length} refused attempt(s) — this is a bug</summary>
              {/* The engine's refusal quotes what was heard, so the dump is
                  redacted like every other surface a grown-up reads. */}
              <pre>
                {redactDeep(JSON.stringify(
                  rejections.map((r) => ({ message: r.message, details: r.details })), null, 1
                ))}
              </pre>
            </details>
          )}
          <p className="bp-panel__fine">
            Turns this session: {stats.trials} · popped {stats.popped} · voice heard {stats.vocalised}
          </p>
        </section>

        <div className="bp-panel__actions">
          <button type="button" className="bp__btn" onClick={onToggleSound}>
            {soundOn ? 'Mute Pip' : 'Unmute Pip'}
          </button>
          <button type="button" className="bp__btn" onClick={onNextLevel}>Next level</button>
          <button type="button" className="bp__btn bp__btn--quiet" onClick={onEndSession}>End session</button>
        </div>
      </div>
    </div>
  );
}
